<template>
    <!-- col 1 -->
    <div class="column-container d-flex order-lg-1 order-1 mr-lg-auto">
        <OfferAmount :amount="amount" />
    </div>

    <!-- purchases, line_of_credit, credit_builder, rewards-->
    <template
        v-if="
            checkOnlyCreditCardProductType('purchases') ||
            checkOnlyCreditCardProductType('line_of_credit') ||
            checkOnlyCreditCardProductType('credit_builder') ||
            checkOnlyCreditCardProductType('rewards')
        "
    >
        <!-- col 2 -->
        <div v-if="ccValue('fee_type') === 'fixed'" class="column-container d-flex order-lg-3 order-3 mr-lg-auto">
            <GenericColumn
                :value="formatPercentage(ccValue('rep_apr'))"
                :label="$t('results.offerBlocks.regularPurchasesApr', { feeType: feeType })"
            />
        </div>

        <div v-else-if="ccValue('purchases_term')" class="column-container d-flex order-lg-2 order-2 mr-lg-auto">
            <GenericColumn :value="purchaseTerm" :label="$t('results.offerBlocks.onPurchases')" />
        </div>

        <div v-else-if="apr" class="column-container d-flex order-lg-2 order-2 mr-lg-auto">
            <GenericColumn
                :value="formatPercentage(apr)"
                :label="$t('results.offerBlocks.regularPurchasesApr', { feeType: feeType })"
            />
        </div>

        <div v-else class="column-container d-flex order-lg-2 order-2 mr-lg-auto">
            <GenericColumn
                :value="formatPercentage(ccValue('rep_apr'))"
                :label="$t('results.offerBlocks.regularPurchasesApr', { feeType: feeType })"
            />
        </div>

        <!-- col 3 -->
        <div class="column-container d-flex order-lg-3 order-3 mr-lg-auto">
            <GenericColumn
                :value="
                    ccValue('purchases_fees')
                        ? formatCurrency(ccValue('purchases_fees'))
                        : $t('results.filtering.noFees')
                "
                :label="feesDurationLabel"
            />
        </div>
    </template>

    <!-- just balance_transfer -->
    <template v-else-if="checkOnlyCreditCardProductType('balance_transfer')">
        <!-- col 2 -->
        <div v-if="ccValue('bt_term')" class="column-container d-flex order-lg-2 order-2 mr-lg-auto">
            <GenericColumn :value="btTerm" :label="$t('results.offerBlocks.onTransfers')" />
        </div>

        <div v-else-if="apr" class="column-container d-flex order-lg-2 order-2 mr-lg-auto">
            <GenericColumn :value="apr" :label="$t('results.offerBlocks.balanceTransferApr', { feeType: feeType })" />
        </div>

        <div v-else-if="ccValue('rep_apr')" class="column-container d-flex order-lg-2 order-2 mr-lg-auto">
            <GenericColumn
                :value="formatPercentage(ccValue('rep_apr'))"
                :label="$t('results.offerBlocks.balanceTransferApr', { feeType: feeType })"
            />
        </div>

        <!-- col 3 -->
        <div class="column-container d-flex order-lg-3 order-3 mr-lg-auto">
            <GenericColumn
                :value="ccValue('bt_fees') ? formatPercentage(ccValue('bt_fees')) : $t('results.filtering.noFees')"
                :label="$t('results.offerBlocks.transferFee')"
            />
        </div>
    </template>

    <!-- both types -->
    <template
        v-if="
            (creditCardProductTypes.includes('purchases') || creditCardProductTypes.includes('rewards')) &&
            creditCardProductTypes.includes('balance_transfer')
        "
    >
        <!-- col 2 -->
        <div v-if="ccValue('bt_apr')" class="column-container d-flex order-lg-2 order-2 mr-lg-auto">
            <GenericColumn
                :value="formatPercentage(ccValue('bt_apr'))"
                :label="$t('results.offerBlocks.balancePurchaseApr', { feeType: feeType })"
            />
        </div>

        <div v-else-if="ccValue('bt_term')" class="column-container d-flex order-lg-2 order-2 mr-lg-auto">
            <GenericColumn :value="btTerm" :label="$t('results.offerBlocks.onTransfers')" />
        </div>

        <!-- col 3 -->
        <div v-if="ccValue('fee_type') === 'fixed'" class="column-container d-flex order-lg-3 order-3 mr-lg-auto">
            <GenericColumn
                :value="formatPercentage(ccValue('rep_apr'))"
                :label="$t('results.offerBlocks.paFixedOnPurchases')"
            />
        </div>

        <div v-else-if="ccValue('purchases_term')" class="column-container d-flex order-lg-3 order-3 mr-lg-auto">
            <GenericColumn :value="purchaseTerm" :label="$t('results.offerBlocks.onPurchases')" />
        </div>

        <div v-else-if="apr" class="column-container d-flex order-lg-3 order-3 mr-lg-auto">
            <GenericColumn :value="apr" :label="$t('results.offerBlocks.balancePurchaseApr', { feeType: feeType })" />
        </div>

        <div v-else class="column-container d-flex order-lg-3 order-3 mr-lg-auto">
            <GenericColumn
                :value="formatPercentage(ccValue('rep_apr'))"
                :label="$t('results.offerBlocks.balancePurchaseApr', { feeType: feeType })"
            />
        </div>
    </template>

    <!-- col 4 -->
    <div class="column-container d-flex order-lg-4 order-4 mr-lg-auto">
        <OfferRatesCreditCardOnly />
    </div>
</template>

<script>
import { formatCurrency, formatPercentage } from '@js/utilities/number.js'
import lowerCase from 'lodash/lowerCase'

export default {
    inject: ['offerGroup', 'offer'],
    computed: {
        creditCardProductTypes() {
            const productType = this.offer?.loan_offer?.credit_card?.product_type
            if (productType && productType.length > 0) {
                return this.offer.loan_offer.credit_card.product_type.split(',')
            }
            return ['purchases']
        },
        amount() {
            return this.rangeValue('credit_limit', formatCurrency) ?? this.$t('results.offerBlocks.creditLimit')
        },
        apr() {
            return this.rangeValue('apr', formatPercentage)
        },
        btTerm() {
            return (
                this.offer?.loan_offer?.credit_card?.bt_term?.concat(' ', this.$t('results.months')) ??
                this.$t('results.offerBlocks.term')
            )
        },
        purchaseTerm() {
            return (
                this.offer?.loan_offer?.credit_card?.purchases_term?.concat(' ', this.$t('results.months')) ??
                this.$t('results.offerBlocks.term')
            )
        },
        feesDurationLabel() {
            switch (this.ccValue('fees_duration')) {
                case 'annual':
                    return this.$t('results.annualFees')
                case 'monthly':
                    return this.$t('results.monthlyFees')
                case 'weekly':
                    return this.$t('results.weeklyFees')
                case 'daily':
                    return this.$t('results.dailyFees')
            }
            return this.$t('results.monthlyFees')
        },
        feeType() {
            return this.ccValue('fee_type') ? lowerCase(this.ccValue('fee_type')) : 'variable'
        },
    },
    methods: {
        formatCurrency: formatCurrency,
        formatPercentage: formatPercentage,
        checkOnlyCreditCardProductType(item) {
            if (this.creditCardProductTypes.length === 0) {
                this.creditCardProductTypes[0] = 'purchases'
            }
            return this.creditCardProductTypes.length === 1 && this.creditCardProductTypes[0] === item
        },
        rangeValue(field, formatFunc) {
            let value = this.ccValue(field)
            value = value ? formatFunc(value) : null

            const valueMin = this.ccValue(`${field}_min`)
            const valueMax = this.ccValue(`${field}_max`)

            let range = null

            if (valueMin != null && valueMax !== null) {
                range = !(parseInt(valueMin) === 0 && parseInt(valueMax) === 0)
                    ? formatFunc(valueMin).concat(' - ', formatFunc(valueMax))
                    : null
            }

            return value ?? (range && valueMin === valueMax ? formatFunc(valueMin) : range)
        },
        ccValue(name) {
            if (this.offer?.loan_offer?.credit_card && name in this.offer.loan_offer.credit_card) {
                return !this.isEmptyValue(this.offer.loan_offer.credit_card[name])
                    ? this.offer.loan_offer.credit_card[name]
                    : null
            }

            return null
        },
    },
}
</script>
